/* tslint:disable:import-spacing component-class-suffix no-inferrable-types variable-name
   typedef-whitespace component-selector no-trailing-whitespace */

import {
  AfterViewInit,
  OnDestroy,
  Component,
  HostListener,
  Input
}                                    from '@angular/core';

import {
  BehaviorSubject,
  Subscription
}                                    from 'rxjs';

import {TSO}                         from '@/models/scenario/t.s.o';

import {NumberRenderer}              from '@/helpers/number.renderer';
import {WholeNumberRenderer}         from '@/helpers/whole.number.renderer';
import {Scenario}                    from '@/models/scenario/scenario';
import {Logger}                      from '@/util/logger';
import {UnDoRedo}                    from '@/util/un.do.redo';
import {NameRenderer}                from '@/helpers/name.renderer';
import {DescriptionRenderer}         from '@/helpers/description.renderer';

import {TSOChange}                   from './t.s.o.change';
import {SummaryTableSelectCommand}   from './summary.table.select.command';
import {SummaryTableUnSelectCommand} from './summary.table.un.select.command';
import {TSOInlineEdit}               from './t.s.o.inline.edit';
import {ICommand}                    from '@/util/i.command';

import {Train} from "@/models/scenario/train";
import {basename} from "@/util/util";
import {TrainClass} from "@/models/scenario/train.class";
import {PeriodRenderer}              from "@/helpers/period.renderer";
import {TrainClassesRenderer}        from "@/helpers/train.classes.renderer";
import {Period}                      from "@/util/period";

import 'ag-grid-community';

import {
  CsvExportParams, ExcelExportParams,
  GetContextMenuItemsParams,
  GridApi,
  GridReadyEvent,
  ProcessCellForExportParams,
  RowClickedEvent,
  RowNode
} from "ag-grid-community";


@Component({
  selector: 'tso-summary-table',
  template: `
    <div id="tso-table-container_{{uuid}}" class="tso-table-container" style="">
      <ag-grid-angular id="ag-grid-tso-table_{{uuid}}" style="width: 100%; height: 100%;"
        class="ag-theme-balham-dark ag-grid-tso-table"
        [rowData]="rowData"
        [columnDefs]="defaultColDefs"
        [defaultColDef]="defaultColDef"
        [context]="context"
        [animateRows]="true"
        [enableRangeSelection]="true"
        [rowSelection]="'single'"

        [allowContextMenuWithControlKey]="true"
        [getContextMenuItems]="getContextMenuItems"

        (gridReady)="onGridReady($event)"
        (selectionChanged)="onSelectionChanged()"
        (rowClicked)="onRowClicked($event)"
      >
      </ag-grid-angular>
    </div>
    `
})

/**
 *
 * @class TSOSummaryTable
 *
 * @brief @brief this table shows a tabular summary of all slow orders in the scenario
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      July 2019
 *
 */
export class TSOSummaryTable implements AfterViewInit, OnDestroy {

  @Input()             uuid: number                          = 0;

  /** the history manager (not owned by us */
  private          _history: UnDoRedo                        = null;

  /** @var _scenario the scenario model */
  private         _scenario: Scenario                       = null;

  /** the currently active row/selection/tSO */
  private       selectedTSO: BehaviorSubject<TSOChange>     = null;
  private         selectSub: Subscription                   = null;

  private    lastInlineEdit: BehaviorSubject<TSOInlineEdit> = null;
  private           editSub: Subscription                   = null;

  /* the row that was previously selected */
  private      oldSelection: any                            = null;

  /** the parent controller/view must provide us with a means to capture the editor input */
  public inputCapture: () => TSOChange;

  /** the parent controller/view must provide usu with a means to restore the editor input */
  public inputRestore: (snapshot: TSOChange)                => void;

  /** the API for the widget */
  private              api: GridApi                         = null;

  private     isFullScreen: boolean                         = false;

  public context = {

    tsoTable: this

  };

  public defaultColDef = {
    sortable:  true,
    filter:    true,
    resizable: true
  };

  public rowData = [];


  public defaultColDefs: any = [

    {
      headerName: 'ID',
      field: 'id',
      editable: false,
      width: 72
    },

    {
      headerName: 'Name',
      field: 'name',
      cellRenderer: NameRenderer,
      editable: false,
      width: 150,
    },

    {
      headerName: 'Tonnage Release',
      field: 'tonnage',
      cellRenderer: WholeNumberRenderer,
      editable: false,
      width: 150,
    },

    {
      headerName: 'Start MP',
      field: 'eastMile',
      cellRenderer: NumberRenderer,
      editable: false,
      width: 150,
    },

    {
      headerName: 'End MP',
      field: 'westMile',
      cellRenderer: NumberRenderer,
      editable: false,
      width: 150,
    },

    {
      headerName: 'Begin',
      field: 'start',
      cellRenderer: PeriodRenderer,
      editable: false,
      width: 150
    },

    {
      headerName: 'Duration',
      field: 'duration',
      cellRenderer: PeriodRenderer,
      editable: false,
      width: 150,
      columnGroupShow: 'open'
    },

    {
      headerName: 'FRT Classes',
      field: 'frtClasses',
      cellRenderer: TrainClassesRenderer,
      editable: false,
      width: 150
    },

    {
      headerName: 'FRT Speed',
      field: 'frtSpeed',
      cellRenderer: NumberRenderer,
      editable: false,
      width: 150,
      columnGroupShow: 'open'
    },

    {
      headerName: 'PSG Classes',
      field: 'psgClasses',
      cellRenderer: TrainClassesRenderer,
      editable: false,
      width: 150,
    },

    {
      headerName: 'PSG Speed',
      field: 'psgSpeed',
      cellRenderer: NumberRenderer,
      editable: false,
      width: 150,
    },

    {
      headerName: 'Description',
      field: 'description',
      cellRenderer: DescriptionRenderer,
      editable: false,
      width: 150,
    },
  ];

  public onGridReady(params: GridReadyEvent): void {

    this.api       = params.api;
  }

  /** helper to fetch the actual TSO for the given row */
  public tsoOfRow(data: any): TSO {

    const id = data?.id ? data.id : 0;

    return Array.from(this._scenario.tso().values()).
      find(x => x._id === id);
  }

  /** allow selection from history */
  public doSelection(selection: TSOChange) {

    /*
     * to allow user inputs to sometimes be different from the current
     * selection; i.e. no selection in summary view, and the user is
     * editing a completely new block...we use the 'snapshot' field; when
     * we emit a work block change...the input editor will look for the
     * snapshot field as an override; if its there, it uses those values
     * rather than the thing being selected.
     *
     * the upshot is that all we have to, is emit the selection (for the
     * other sub-panels), and locally select the implied row...or clear
     * the selection if there is nothing selected.
     *
     * We don't have to worry about interaction / timing with the other agGrid
     * events...because we haven't added any mechanics there.  It's all done
     * via RowClicked event, effectively doSelection() is  fake user click
     * on the row...but from history.
     *
     */

    /* adjust the summary table selection to this point in the history */

    if ((selection === null) || (selection?.id === null) || isNaN(selection.id)) {

      /* there is no row selected, just clear the selection */

      this.api.deselectAll();

      this.oldSelection = null;

    } else {

      /* select the implied row */

      const tso: TSO = this.tsoOfRow(selection);

      this.api.forEachNode((node: RowNode): void => {

        if (node.data.id !== tso._id) {

          return;
        }

        node.setSelected(true, true);

        this.oldSelection = node.data;

      });

    }

    /* emit the selection so that other panels also sync up with this position in the history */

    this.selectedTSO.next(selection);

  }

  /** fetch the current selection */
  public getSelection(): TSOChange {

    const  data: TSOChange = new TSOChange({
      src:         null,
      id:          null,
      name:        null,
      description: null,
      start:       null,
      duration:    null,
      tonnage:     null,
      eastMile:    null,
      eastDP:      null,
      westMile:    null,
      westDP:      null,
      frtSpeed:    null,
      psgSpeed:    null,
      frtClasses:  null,
      psgClasses:  null,
      snapshot:    this.inputCapture(),
      model:       null
    });

    /* is anything selected? */

    if (this.oldSelection === null) {

      /* we have the selection */

      return data;
    }

    /* if something is selected, then we fill in selection */

    const tso: TSO = this.tsoOfRow(this.oldSelection);

    let psgClasses = [];
    let frtClasses = [];

    if(tso.speeds.has('FRT')) {

      frtClasses = Array.from(tso.speeds.get('FRT').values()).map(x => x._trainClass);
    }

    if(tso.speeds.has('PSG')) {

      psgClasses = Array.from(tso.speeds.get('PSG').values()).map(x => x._trainClass);
    }

    data.src               = this;
    data.id                = tso._id;
    data.name              = tso._name;
    data.start             = Period.fromSeconds(tso._start);
    data.duration          = Period.fromSeconds(tso._duration);
    data.tonnage           = (tso._tonnage >= 0)  ? tso._tonnage : 0;
    data.eastMile          = (tso._eastMile >= 0) ? tso._eastMile : 0;
    data.eastDP            = tso._eastDP;
    data.westMile          = (tso._westMile >= 0) ? tso._westMile : 0;
    data.westDP            = tso._westDP;
    data.frtSpeed          = (tso._frtSpeed >= 0) ? tso._frtSpeed : 0;
    data.frtClasses        = frtClasses;
    data.psgSpeed          = (tso._psgSpeed >= 0) ? tso._psgSpeed : 0;
    data.psgClasses        = psgClasses;
    data.description       = tso._description;

    return data;
  }

  /** clear the selection (if any) */
  public clearSelection() {

    if (this.oldSelection === null) {

      /* nothing to do */

      return ;
    }

    /* the old value (the existing selection) might be empty */

    const  empty: TSOChange = new TSOChange({
      src:         null,
      id:          null,
      name:        null,
      description: null,
      start:       null,
      duration:    null,
      tonnage:     null,
      eastMile:    null,
      eastDP:      null,
      westMile:    null,
      westDP:      null,
      frtSpeed:    null,
      psgSpeed:    null,
      frtClasses:  null,
      psgClasses:  null,
      snapshot:    null,
      model:       null
    });

    let oldValue: TSOChange;
    let newValue: TSOChange;
    let tso:      TSO;

    /* capture the old value */

    /* snapshot is not null, so emitting this one will make the user input take on the snapshot */

    tso      = this.tsoOfRow(this.oldSelection);

    let psgClasses = [];
    let frtClasses = [];

    if(tso.speeds.has('FRT')) {

      frtClasses = Array.from(tso.speeds.get('FRT').values()).map(x => x._trainClass);
    }

    if(tso.speeds.has('PSG')) {

      psgClasses = Array.from(tso.speeds.get('PSG').values()).map(x => x._trainClass);
    }

    oldValue = new TSOChange({
      src:               this,
      id:                tso._id,
      name:              tso._name,
      start:             Period.fromSeconds(tso._start),
      duration:          Period.fromSeconds(tso._duration),
      tonnage:           (tso._tonnage >= 0)  ? tso._tonnage : 0,
      eastMile:          (tso._eastMile >= 0) ? tso._eastMile : 0,
      eastDP:            tso._eastDP,
      westMile:          (tso._westMile >= 0) ? tso._westMile : 0,
      westDP:            tso._westDP,
      frtSpeed:          (tso._frtSpeed >= 0) ? tso._frtSpeed : 0,
      psgSpeed:          (tso._psgSpeed >= 0) ? tso._psgSpeed : 0,
      description:       tso._description,
      frtClasses:        frtClasses,
      psgClasses:        psgClasses,
      snapshot:          this.inputCapture()
    });

    /* capture the new value */

    newValue = empty;

    /* de-select */

    this.api.deselectAll();

    this.oldSelection = null;

    /* it's now safe to signal the selection change */

    this.selectedTSO.next(newValue);

    /* track the history of selecting something new */

    const cmd: ICommand = new SummaryTableUnSelectCommand(oldValue, newValue, this);

    /* track the change */

    this._history.insert(cmd);
  }

  /**
   *  handle a user clicking a row, which just toggles it to selected or no selection at all
   *  because we only allow single row selection.
   *
   */
  public onRowClicked(e: RowClickedEvent) {

    /* the old value (the existing selection) might be empty */

    const  empty: TSOChange = new TSOChange({
      src:         null,
      id:          null,
      name:        null,
      description: null,
      start:       null,
      duration:    null,
      tonnage:     null,
      eastMile:    null,
      eastDP:      null,
      westMile:    null,
      westDP:      null,
      frtSpeed:    null,
      psgSpeed:    null,
      frtClasses:  null,
      psgClasses:  null,
      snapshot:    null,
      model:       null
    });

    let oldValue: TSOChange;
    let newValue: TSOChange;
    let tso:      TSO       = null;

    /* we only do single select, so if the row we click on is already selected...we are deselecting */

    if ((this.oldSelection !== null) && (this.oldSelection.id === e.data.id)) {

      /* capture the old value */

      /* snapshot is not null, so emitting this one will make the user input take on the snapshot */

      tso      = this.tsoOfRow(e.data);

      let psgClasses = [];
      let frtClasses = [];

      if(tso.speeds.has('FRT')) {

        frtClasses = Array.from(tso.speeds.get('FRT').values()).map(x => x._trainClass);
      }

      if(tso.speeds.has('PSG')) {

        psgClasses = Array.from(tso.speeds.get('PSG').values()).map(x => x._trainClass);
      }

      oldValue = new TSOChange({
        src:               this,
        id:                tso._id,
        name:              tso._name,
        start:             Period.fromSeconds(tso._start),
        duration:          Period.fromSeconds(tso._duration),
        tonnage:           (tso._tonnage >= 0)  ? tso._tonnage : 0,
        eastMile:          (tso._eastMile >= 0) ? tso._eastMile : 0,
        eastDP:            tso._eastDP,
        westMile:          (tso._westMile >= 0) ? tso._westMile : 0,
        westDP:            tso._westDP,
        frtSpeed:          (tso._frtSpeed >= 0) ? tso._frtSpeed : 0,
        psgSpeed:          (tso._psgSpeed >= 0) ? tso._psgSpeed : 0,
        description:       tso._description,
        frtClasses:        frtClasses,
        psgClasses:        psgClasses,
        snapshot:          this.inputCapture()
      });

      /* capture the new value */

      newValue = empty;

      /* de-select */

      this.api.deselectAll();

      this.oldSelection = null;

    }  else {

      /* capture the old state */

      if (this.oldSelection === null) {

        oldValue          = Object.assign({}, empty);
        oldValue.snapshot = this.inputCapture();

      } else {

        /* snapshot is not null, so emitting this one will make the user input take on the snapshot */

        tso       = this.tsoOfRow(this.oldSelection);

        let psgClasses = [];
        let frtClasses = [];

        if(tso.speeds.has('FRT')) {

          frtClasses = Array.from(tso.speeds.get('FRT').values()).map(x => x._trainClass);
        }

        if(tso.speeds.has('PSG')) {

          psgClasses = Array.from(tso.speeds.get('PSG').values()).map(x => x._trainClass);
        }

        oldValue  = new TSOChange({
          src:               this,
          id:                tso._id,
          name:              tso._name,
          start:             Period.fromSeconds(tso._start),
          duration:          Period.fromSeconds(tso._duration),
          tonnage:           (tso._tonnage >= 0)  ? tso._tonnage : 0,
          eastMile:          (tso._eastMile >= 0) ? tso._eastMile : 0,
          eastDP:            tso._eastDP,
          westMile:          (tso._westMile >= 0) ? tso._westMile : 0,
          westDP:            tso._westDP,
          frtSpeed:          (tso._frtSpeed >= 0) ? tso._frtSpeed : 0,
          psgSpeed:          (tso._psgSpeed >= 0) ? tso._psgSpeed : 0,
          description:       tso._description,
          frtClasses:        frtClasses,
          psgClasses:        psgClasses,
          snapshot:          this.inputCapture()
        });

        /*  agGrid will do its usual selection change events */
      }

      /* capture the new state, when snapshot is null the inputs will be newValue */

      tso      = this.tsoOfRow(e.data);

      let psgClasses = [];
      let frtClasses = [];

      if(tso.speeds.has('FRT')) {

        frtClasses = Array.from(tso.speeds.get('FRT').values()).map(x => x._trainClass);
      }

      if(tso.speeds.has('PSG')) {

        psgClasses = Array.from(tso.speeds.get('PSG').values()).map(x => x._trainClass);
      }

      newValue = new TSOChange({
        src:               this,
        id:                tso._id,
        name:              tso._name,
        start:             Period.fromSeconds(tso._start),
        duration:          Period.fromSeconds(tso._duration),
        tonnage:           (tso._tonnage >= 0)  ? tso._tonnage : 0,
        eastMile:          (tso._eastMile >= 0) ? tso._eastMile : 0,
        eastDP:            tso._eastDP,
        westMile:          (tso._westMile >= 0) ? tso._westMile : 0,
        westDP:            tso._westDP,
        frtSpeed:          (tso._frtSpeed >= 0) ? tso._frtSpeed : 0,
        psgSpeed:          (tso._psgSpeed >= 0) ? tso._psgSpeed : 0,
        description:       tso._description,
        frtClasses:        frtClasses,
        psgClasses:        psgClasses,
        snapshot:    null
      });

      this.oldSelection = e.data;
    }

    /* it's now safe to signal the selection change */

    this.selectedTSO.next(newValue);

    /* track the history of selecting something new */

    const cmd: ICommand = new SummaryTableSelectCommand(oldValue, newValue, this);

    /* track the change */

    this._history.insert(cmd);
  }

  public onSelectionChanged() {

    if (this.oldSelection === null) {

      return;
    }

    /* keep the currently selected row selected */

    this.api.forEachNode((node: RowNode): void => {

      if (node.data.id === this.oldSelection.id) {

        node.setSelected(true, true);
      }

    });
  }

  public toggleFullScreen() {

    if (this.isFullScreen) {

      Logger.debug('[tso/table] exiting full screen...');

      document.exitFullscreen();

    } else {

      Logger.debug('[tso/table] going full screen...');

      const elem = document.getElementById(`ag-grid-tso-table_${this.uuid}`);

      if (elem.requestFullscreen) {

        elem.requestFullscreen();
      }
    }

  }

  public getContextMenuItems(params: GetContextMenuItemsParams): any {

    const     context: any[] = [];
    const gcTSOTable        = params.context.tsoTable;

    if (context.length !== 0) {

      context.push('separator');
    }

    /* full screen control */

    let label: string = 'Show Full Screen';

    if (gcTSOTable.isFullScreen) {

      label = 'Exit Full Screen';
    }

    context.push( {
      name: label,
      action: () => {

        gcTSOTable.toggleFullScreen();
      }
    });

    context.push('separator');

    const general: any[] = [

      'copy',
      'copyWithHeaders',
      'paste',

      'separator',

      'expandAll',
      'contractAll',

      'separator'
    ];

    context.push.apply(context, general);

    /* add in our custom export functions ... */

    context.push( {
      name: `CSV Export`,
      action: (): void => {

        gcTSOTable.csvExport();
      }
    });

    context.push( {
      name: `Excel Export`,
      action: (): void => {

        gcTSOTable.excelExport();
      }
    });

    /* add remaining common / generic actions */

    const general2: any[] = [

      'separator',

      'autoSizeAll'
    ];

    /* pass back the merged menu */

    context.push.apply(context, general2);

    return context;
  }

  ngOnDestroy(): void {

    if (this.selectSub !== null) {

      /* unsubscribe our observer */

      this.selectSub.unsubscribe();
    }

    if (this.editSub !== null) {

      /* unsubscribe our observer */

      this.editSub.unsubscribe();
    }
  }

  /** @brief first time setup of view */
  ngAfterViewInit() {

    Logger.debug('[tso/table] view init...');

    this.clear();

    const        elem = document.getElementById(`ag-grid-tso-table_${this.uuid}`);
    const gcTSOTable = this;

    elem.addEventListener('fullscreenchange', () => {

      gcTSOTable.isFullScreen = document.fullscreenElement != null;

      Logger.debug('[tso/table] (elem) flipped full screen mode: ', gcTSOTable.isFullScreen);

    }, true);
  }

  /** @brief reset the view to empty */
  public clear() {

    this._scenario = null;
  }

  /** @brief standard destructor */
  destroy() {

    Logger.debug('[tso/table] cleaning up...');

  }

  /** format one of the table column values as a simple string (i.e. for export) */
  public format(value: any): string {

    if (Array.isArray(value) && value.length > 0 && value[0] instanceof Train) {

      /* format a train list */

      return (value as Train[]).map((train: Train): string => {

        return train.quickName();

      }).join(',');

    }

    if (value instanceof Period) {

      const d = PeriodRenderer.pad(value._days);
      const h = PeriodRenderer.pad(value._hours);
      const m = PeriodRenderer.pad(value._minutes);
      const s = PeriodRenderer.pad(value._secs);

      return`${d}:${h}:${m}:${s}`;
    }

    if (value instanceof TrainClass) {

      return value._name;
    }

    if (Array.isArray(value) && value.length > 0 && value[0] instanceof TrainClass) {

      return (value as TrainClass[]).map((tc: TrainClass): string => {

        return tc._name;

      }).join(',');
    }

    /* fell through, we don't know how to format this thing. */

    Logger.error('[tso/table] can not format cell', value);

    return '';
  }

  /** export the table to a CSV download */
  public csvExport(): void {

    let tst: TSOSummaryTable = this;

    Logger.debug('[tso/table] CSV export...');

    /* ref: https://www.ag-grid.com/angular-data-grid/csv-export/#csvexportparams */

    let fileName: string = `${basename(tst._scenario._tag)}.csv`;

    let params: CsvExportParams = {
      allColumns: true,
      fileName,
      processCellCallback: (params: ProcessCellForExportParams): string => {

        if (typeof params.value == 'number' || typeof params.value == 'string') {

          return params.value as string;
        }

        return tst.format(params.value);
      }
    };

    tst.api.exportDataAsCsv(params);
  }

  /** export the table to an Excel download */
  public excelExport(): void {

    let tst: TSOSummaryTable = this;

    Logger.debug('[tso/table] EXCEL export...');

    /* ref: https://www.ag-grid.com/angular-data-grid/csv-export/#csvexportparams */

    let fileName: string = `${basename(tst._scenario._tag)}.xlsx`;

    let params: ExcelExportParams = {
      allColumns: true,
      fileName,
      processCellCallback: (params: ProcessCellForExportParams): string => {

        if (typeof params.value == 'number' || typeof params.value == 'string') {

          return params.value as string;
        }

        return tst.format(params.value);
      }
    };

    tst.api.exportDataAsExcel(params);
  }

  /** take a reference to the shared history manager (provides undo/redo support) */
  public setHistory(history: UnDoRedo): void {

    this._history = history;
  }

  /** @brief set the table base on this scenario */
  setScenario(scenario: Scenario = null): boolean {

    Logger.debug('[tso/table] setting scenario...');

    if (scenario === null) {

      this.clear();

      return true;
    }

    /* save the model */

    this._scenario                   = scenario;

    /* refresh the table based on currently selected train class */

    this.refreshTable();

    /* all done */

    return true;
  }

  /** handle single column edits in the edit input area */
  setInlineEditSubject(edit: BehaviorSubject<TSOInlineEdit>) {

    if (this.editSub !== null) {

      /* unsubscribe old one */

      this.editSub.unsubscribe();
    }

    /* subscribe to this selection watcher ... */

    this.lastInlineEdit = edit;
    this.editSub        = edit.subscribe( (next: TSOInlineEdit) => {

      this.inlineEdited(next);
    });
  }

  /** handle single column changes in the editing inputs */
  public inlineEdited(edit: TSOInlineEdit) {

    if (edit === null) {

      /* nothing to do */

      return ;
    }

    Logger.debug('[tso/table] inline edit', edit);

    let datum:        any = edit.value;
    let dataField: string = edit.field;

    /* get the row for this TSO */

    this.api.forEachNode((node: RowNode): void => {

      if (node.data.id !== edit.id) {

        return;
      }

      /* if the make bad value for tonnage or speed, just show it as blank */

      if (['tonnage', 'frtSpeed', 'psgSpeed'].includes(dataField)) {

        if (typeof datum === 'number') {

          if (datum < 0) {

            datum = '';
          }
        }

      }

      if ((datum !== '') && (dataField === 'tonnage')) {

        datum = Math.floor(datum);
      }

      /* twitch the value (without any change events) */

      node.data[dataField] = datum;

      this.api.redrawRows({rowNodes: [node]});

    });

  }

  /** set a new observer to use for watching for selection changes */
  public setSelectionSubject(selection: BehaviorSubject<TSOChange>) {

    if (this.selectSub !== null) {

      /* unsubscribe old one */

      this.selectSub.unsubscribe();
    }

    /* subscribe to this selection watcher ... */

    this.selectedTSO = selection;
    this.selectSub   = selection.subscribe( (next: TSOChange) => {

      this.setSelected(next);
    });

  }

  /** handle somebody change the selection somewhere */
  public setSelected(change: TSOChange) {

    if (change === null) {

      /* just clear the selection */

      Logger.debug('[tso/table:set] clearing selection');

      this.api.deselectAll();
      this.oldSelection = null;

      return ;
    }

    /* check that we aren't calling ourselves */

    if ( (change.src instanceof TSOSummaryTable) && (change.src === this)) {

      /* from self, ignore infinite looping! */

      return ;
    }

  }

  /** @brief helper to update the table  */
  public refreshTable(): boolean {

    Logger.debug('[tso/table] refreshing on', this._scenario);

    /* start with standard columns ... */

    const cd: any[] = [].concat([], this.defaultColDefs);

    /* ok, we can now tell agGrid what our columns are ... */

    this.api.setGridOption('rowData', []);
    this.api.setGridOption('columnDefs', cd);

    /* now just build out the row data ... */

    this.rowData = [];

    let row: number = 0;

    for (const tso of this._scenario.tso().values()) {

      /* everything except active days */

      let psgClasses = [];
      let frtClasses = [];

      if(tso.speeds.has('FRT')) {

        frtClasses = Array.from(tso.speeds.get('FRT').values()).map(x => x._trainClass);
      }

      if(tso.speeds.has('PSG')) {

        psgClasses = Array.from(tso.speeds.get('PSG').values()).map(x => x._trainClass);
      }

      const rd: any = {
        id:          tso._id,
        name:        tso._name,
        eastMile:    tso._eastMile,
        westMile:    tso._westMile,
        start:       Period.fromSeconds(tso._start),
        duration:    Period.fromSeconds(tso._duration),
        tonnage:     (tso._tonnage < 0) ? '' : Math.floor(tso._tonnage),
        frtClasses:  frtClasses,
        frtSpeed:    (tso._frtSpeed < 0) ? '' : tso._frtSpeed,
        psgClasses:  psgClasses,
        psgSpeed:    (tso._psgSpeed < 0) ? '' : tso._psgSpeed,
        description: tso._description,
        row
      };

      row++;

      this.rowData.push(rd);

    } /* end of tso walk */

    this.api.setGridOption('rowData', this.rowData);

    /* all done */

    return true;
  }

  @HostListener('window:resize')
  public resize() {

    Logger.debug('[tso/table]', 'resizing...');

    /* for now use horizontal scroll, table is getting really wide */
    /* this.gridApi.sizeColumnsToFit(); */
  }

}
